import { useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { useSearchParams } from 'react-router-dom';
import { AdminHeader } from '../../adminComponents/Layout/AdminLayout/AdminHeader';
import {
  AdminSpinner,
  ListSpinner,
} from '../../adminComponents/AdminSpinner/adminSpinner';
// Direct import: this one is deliberately outside the barrel.
import { NoDataComponent } from '../../adminComponents/NoDataComponent/NoDataComponent';
import { Tab, TabsWrapper } from '../../styles/styles';
import { NotificationRow } from './components/NotificationRow';
import { useNotificationActions } from './hooks/useNotificationActions';
import { useNotificationsList } from './hooks/useNotificationsList';
import { useOpenFromPush } from './hooks/useOpenFromPush';
import { useNotificationsUnreadCount } from './hooks/useNotificationsUnreadCount';
import { TextAction } from './notificationActions.styled';

/** `?filter=unread` - in the URL so Back from a notification keeps the tab. */
const FILTER_PARAM = 'filter';
const UNREAD = 'unread';

/**
 * Everything the bell has had, a page at a time. The same list the dropdown
 * shows the head of, and the same cache.
 */
export const Notifications = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const unreadOnly = searchParams.get(FILTER_PARAM) === UNREAD;
  const unread = useNotificationsUnreadCount() ?? 0;
  const list = useNotificationsList({ unreadOnly });
  const actions = useNotificationActions();
  useOpenFromPush(actions.open);

  const [sentinelRef, sentinelInView] = useInView({ rootMargin: '400px' });

  useEffect(() => {
    if (sentinelInView && list.hasNextPage && !list.isFetchingNextPage) {
      list.fetchNextPage();
    }
  }, [sentinelInView, list]);

  const showUnread = (value: boolean) =>
    setSearchParams(
      (params) => {
        if (value) params.set(FILTER_PARAM, UNREAD);
        else params.delete(FILTER_PARAM);
        return params;
      },
      { replace: true }
    );

  return (
    <>
      <AdminHeader title="Notifications">
        {unread > 0 && (
          <TextAction
            type="button"
            onClick={actions.markAll}
            disabled={actions.markingAll}
          >
            Mark all as read
          </TextAction>
        )}
      </AdminHeader>

      <TabsWrapper>
        <Tab selected={!unreadOnly} onClick={() => showUnread(false)}>
          All
        </Tab>
        <Tab selected={unreadOnly} onClick={() => showUnread(true)}>
          {unread > 0 ? `Unread (${unread})` : 'Unread'}
        </Tab>
      </TabsWrapper>

      {list.isLoading && <AdminSpinner />}

      {list.items.map((notification) => (
        <NotificationRow
          key={notification.id}
          notification={notification}
          onOpen={actions.open}
        />
      ))}

      {!list.isLoading && list.items.length === 0 && (
        <NoDataComponent
          title={list.isError ? 'Something went wrong' : 'Nothing here'}
          description={
            list.isError
              ? 'Notifications could not be loaded.'
              : unreadOnly
              ? 'You are all caught up.'
              : 'No notifications yet.'
          }
        />
      )}

      <ListSpinner
        loading={list.isFetchingNextPage}
        observerRef={sentinelRef}
      />
    </>
  );
};
